<script lang="ts">
import { getRegulatorLicenseApplyRequirements, type LicenseApplyRequirementItem } from '~/api/license-application'

type LicenseApplyClickOptions = {
  urlSlug: string
  licenseApplicationPath: string
  openDialog: (items: LicenseApplyRequirementItem[]) => void
}

/** 获取 HTML 去标签、去空白后的纯文本字符数。 */
export const getHtmlTextLength = (html: unknown): number => {
  const text = html === null || html === undefined ? '' : String(html)

  return text
    .replace(/<[^>]*>/g, '')
    .replace(/&(?:nbsp|#160);/gi, '')
    .replace(/\s+/g, '').length
}

/** 获取申请要求并根据内容长度跳转牌照申请页或打开申请内容弹窗。 */
export const handleLicenseApplyClick = async ({
  urlSlug,
  licenseApplicationPath,
  openDialog,
}: LicenseApplyClickOptions) => {
  const response = await getRegulatorLicenseApplyRequirements({ urlSlug }).catch(() => null)
  const items = (response?.data?.items || response?.items || []).filter((item) => item.title || item.content)
  const contentLength = items.reduce((length, item) => length + getHtmlTextLength(item.content), 0)

  if (contentLength > 1200) {
    if (import.meta.client) {
      window.open(licenseApplicationPath, '_blank', 'noopener,noreferrer')
    } else {
      await navigateTo(licenseApplicationPath)
    }

    return { items, contentLength }
  }

  openDialog(items)
  return { items, contentLength }
}
</script>

<script setup lang="ts">
import type { RegulatorDetailLinkItem } from '~/api/regulator-list'
import type { LicenseApplyRequirementItem } from '~/api/license-application'
import ArrowRight from '~/assets/icon/arrow-right.svg'

const props = withDefaults(
  defineProps<{
    items?: RegulatorDetailLinkItem[]
    urlSlug?: string
  }>(),
  {
    items: () => [],
    urlSlug: '',
  },
)

const { isRtl } = useRtl()
const { localePath } = useAppI18n()

const displayItems = computed(() => props.items.filter((item) => item.text).slice(0, 6))

const licenseApplicationPath = computed(() =>
  localePath({
    name: 'disclosure-license-apply',
    params: {
      slug: props.urlSlug,
    },
  }),
)
const isApplyDialogVisible = ref(false)
const isLicenseApplyLoading = ref(false)
const applyDialogItems = ref<LicenseApplyRequirementItem[]>([])

/** 打开牌照申请内容弹窗。 */
const openApplyDialog = (items: LicenseApplyRequirementItem[]) => {
  applyDialogItems.value = items
  isApplyDialogVisible.value = true
}

/** 所有申请入口统一按内容长度跳转牌照申请页或打开弹窗。 */
const handleApplyItemClick = async () => {
  if (isLicenseApplyLoading.value) {
    return
  }

  if (!props.urlSlug) {
    if (import.meta.client) {
      window.open(licenseApplicationPath.value, '_blank', 'noopener,noreferrer')
    } else {
      await navigateTo(licenseApplicationPath.value)
    }

    return
  }

  isLicenseApplyLoading.value = true

  try {
    await handleLicenseApplyClick({
      urlSlug: props.urlSlug,
      licenseApplicationPath: licenseApplicationPath.value,
      openDialog: openApplyDialog,
    })
  } finally {
    isLicenseApplyLoading.value = false
  }
}
</script>

<template>
  <section
    class="w-full max-w-[384px] rounded-[12px] border-[1px] border-[rgba(11,18,40,0.14)] bg-[#ffffff] p-[24px]"
    aria-labelledby="license-application-title"
  >
    <header class="flex items-center justify-between border-b-[0.5px] border-[rgba(5,21,36,0.06)] pb-[15px]">
      <h2 id="license-application-title" class="m-[0] text-[20px] leading-[28px] font-[600] text-[#292a2e]">
        {{ $t('84650') }}
      </h2>
      <button
        type="button"
        class="flex size-[20px] cursor-pointer items-center justify-center border-[0] bg-transparent p-[0] text-[#7d818a] transition-opacity hover:opacity-70"
        :disabled="isLicenseApplyLoading"
        aria-label="查看牌照申请详情"
        @click="handleApplyItemClick"
      >
        <ArrowRight class="size-[20px]" :class="{ 'rotate-180': isRtl }" aria-hidden="true" />
      </button>
    </header>

    <div class="mt-[12px] grid grid-cols-2 gap-[12px]">
      <template v-for="(item, index) in displayItems" :key="`${item.type}-${item.text}-${index}`">
        <button
          type="button"
          class="flex h-[82px] cursor-pointer flex-col items-start justify-center rounded-[4px] bg-[#f8f8f8] p-[16px] text-left text-inherit no-underline"
          @click="handleApplyItemClick"
        >
          <CommonImage
            v-if="item.icon"
            :src="item.icon"
            :alt="item.text"
            class="size-[20px]"
            width="20"
            height="20"
            object-fit="contain"
            loading="eager"
          />
          <h3 class="m-[0] mt-[8px] text-[14px] leading-[20px] font-[500] text-[#292a2e]">{{ item.text }}</h3>
        </button>
      </template>
    </div>

    <RegulatorsApplyDialog v-model="isApplyDialogVisible" :items="applyDialogItems" />
  </section>
</template>
